import type { MouseEvent, ReactNode } from 'react';
import { useMemo } from 'react';

import { ApplicationLogo } from '@ui/uikit/components/applications/common/ApplicationLogo';
import { cn } from '@ui/uikit/utils';

import { Card } from '../../Card';
import { DateTime } from '../../DateTime';
import { type FeedbackButtonGroupSelection } from '../../FeedbackButtonGroup';
import { Rating } from '../../Rating';
import Reactions, { ReactionsGroup, ReactionsOption } from '../../Reactions/internals/Reactions';
import { UserAvatar } from '../../UserAvatar';
import { type FeedbackType } from '../../VotingDropdownMenu/internals/ReportDialog/ReportDialog';
import styles from './styles.module.scss';

interface ReviewReactions {
  positive?: number;
  negative?: number;
  funny?: number;
}

interface ReviewActiveReactions {
  positive: boolean;
  negative: boolean;
  funny: boolean;
}

interface ReviewAuthor {
  name: string | null;
  icon: string;
  karma?: number;
  profileLink?: string;
  membership?: "FREE" | "PLUS" | "PREMIUM" | "PREMIUM_PLUS";
}

type ReviewCardSize = 'small' | 'large';

interface ReviewCardProps {
  id?: number;
  appName: string;
  logoUrl?: string;
  size?: ReviewCardSize;
  rating?: number;
  excerpt?: string;
  date?: Date;
  origin?: string;
  author?: ReviewAuthor;
  reactions?: ReviewReactions;
  activeReactions?: ReviewActiveReactions;
  sharingControl?: ReactNode;
  reportingControl?: ReactNode;
  feedbackType: FeedbackType;
  onOpenReview?: (id: number) => void;
  isCursorPointer?: boolean;
  onHelpfulnessToggle?: (selection: FeedbackButtonGroupSelection) => void;
  onFunnyReactionToggle?: (state: 'on' | 'off') => void;
}

const ReviewCard = ({
  id,
  appName,
  logoUrl,
  size = 'small',
  rating,
  excerpt,
  date,
  origin,
  author,
  reactions,
  activeReactions,
  sharingControl,
  reportingControl,
  feedbackType,
  onOpenReview,
  isCursorPointer,
  onHelpfulnessToggle,
  onFunnyReactionToggle,
}: ReviewCardProps) => {
  const handleOpenReview = () => {
    onOpenReview && id && onOpenReview(id);
  };

  const selectedHelpfulness = useMemo<FeedbackButtonGroupSelection>(() => {
    if (activeReactions?.positive) {
      return 'positive';
    }

    if (activeReactions?.negative) {
      return 'negative';
    }

    return 'none';
  }, [activeReactions?.positive, activeReactions?.negative]);

  const handleFunnyReactionToggle = (state: 'on' | 'off', event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onFunnyReactionToggle && onFunnyReactionToggle(state);
  };

  const handleHelpfulnessToggle = (
    selection: FeedbackButtonGroupSelection,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    onHelpfulnessToggle && onHelpfulnessToggle(selection);
  };

  return (
    <Card
      onClick={handleOpenReview}
      className={cn(styles['self'], { [styles['cursorPointer']]: isCursorPointer })}
    >
      <div>
        <div className={styles['header']}>
          {size === 'small' && (
            <div className={styles['header__row']}>
              <div>
                <ApplicationLogo url={logoUrl || ''} size="small" />
                {appName.toUpperCase()}
              </div>
              <div className={styles['controls']}>
                {reportingControl}
                {sharingControl}
              </div>
            </div>
          )}
          <div className={styles['header__row']}>
            <div>
              {rating != null && (
                <div className={styles['rating']}>
                  <Rating.Indicator value={rating} />
                </div>
              )}

              <div className={styles['date']}>
                {date && <DateTime date={date} dateFormat="medium" />}
              </div>
            </div>
            {size === 'large' && (
              <div className={styles['controls']}>
                {reportingControl}
                {sharingControl}
              </div>
            )}
          </div>
        </div>
        <div className={styles['excerpt']}>{excerpt}</div>
      </div>

      <div className={styles['footer']}>
        {author && <UserAvatar user={author} onClick={(e) => e.stopPropagation()} />}
        {reactions && (
          <Reactions>
            <ReactionsGroup onToggle={handleHelpfulnessToggle} selection={selectedHelpfulness}>
              <ReactionsOption
                key={1}
                variant="positive"
                size="small"
                count={reactions.positive}
                label={`Like this ${feedbackType.toLowerCase()}`}
              />
              <ReactionsOption
                key={2}
                variant="negative"
                size="small"
                count={reactions.negative}
                label={`Dislike this ${feedbackType.toLowerCase()}`}
              />
            </ReactionsGroup>
            <ReactionsOption
              key={3}
              variant="funny"
              size="small"
              count={reactions?.funny}
              state={activeReactions?.funny ? 'on' : 'off'}
              onToggle={handleFunnyReactionToggle}
              label={`This ${feedbackType.toLowerCase()} is funny`}
            />
          </Reactions>
        )}
      </div>
    </Card>
  );
};

export { ReviewCard };
